import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { QrCode, ScanLine, Share2, Copy, Loader2 } from 'lucide-react';

const QR_PREFIX = 'gcc:contact:';

interface ContactQRDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ContactQRDialog = ({ open, onOpenChange }: ContactQRDialogProps) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [tab, setTab] = useState<'my' | 'scan'>('my');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerElementId = 'contact-qr-scanner-region';

  const myPayload = profile?.phone ? `${QR_PREFIX}${profile.phone}` : '';

  // Generate QR
  useEffect(() => {
    if (!open || tab !== 'my' || !myPayload) return;
    QRCode.toDataURL(myPayload, {
      width: 320,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then(setQrDataUrl)
      .catch((e) => console.error('QR gen failed', e));
  }, [open, tab, myPayload]);

  // Stop scanner when dialog closes or tab changes
  useEffect(() => {
    if (!open || tab !== 'scan') {
      stopScanner();
    }
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) {
        // ignore
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    if (scannerRef.current) return;
    try {
      setScanning(true);
      const html5 = new Html5Qrcode(scannerElementId);
      scannerRef.current = html5;
      await html5.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          handleScanned(decodedText);
        },
        () => {
          // scan failure per frame — ignore
        }
      );
    } catch (e: any) {
      console.error('Scanner start failed', e);
      setScanning(false);
      toast({
        title: t('error'),
        description: e?.message || 'Could not access camera',
        variant: 'destructive',
      });
    }
  };

  const handleScanned = async (text: string) => {
    if (processing) return;
    if (!text.startsWith(QR_PREFIX)) {
      toast({
        title: t('error'),
        description: 'Not a Global Chat Connect contact code',
        variant: 'destructive',
      });
      return;
    }
    const phone = text.slice(QR_PREFIX.length).trim();
    if (!phone || !user) return;

    setProcessing(true);
    await stopScanner();

    try {
      const { data: result, error } = await supabase.rpc('get_user_by_phone', {
        phone_number: phone,
      });
      if (error) throw error;

      const contact = result && result.length > 0
        ? { id: result[0].user_id, name: result[0].user_name }
        : null;

      if (!contact) {
        toast({ title: t('userNotFound'), description: phone });
        onOpenChange(false);
        navigate('/add-contact');
        return;
      }

      if (contact.id === user.id) {
        toast({ title: t('error'), description: 'This is your own code', variant: 'destructive' });
        setProcessing(false);
        return;
      }

      // Find or create 1-on-1 chat via secure RPC
      const { data: chatId, error: rpcError } = await supabase.rpc(
        'find_or_create_one_on_one_chat',
        { _other_user_id: contact.id }
      );
      if (rpcError) throw rpcError;

      onOpenChange(false);
      navigate(`/chat/${chatId}`);
    } catch (e: any) {
      console.error('Scan handling failed', e);
      toast({ title: t('error'), description: e.message, variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleShare = async () => {
    if (!myPayload) return;
    const shareText = `Add me on Global Chat Connect: ${profile?.phone}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My contact', text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast({ title: 'Copied', description: shareText });
      }
    } catch {
      // user cancelled
    }
  };

  const handleCopy = async () => {
    if (!profile?.phone) return;
    await navigator.clipboard.writeText(profile.phone);
    toast({ title: 'Copied', description: profile.phone });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Contact QR Code</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'my' | 'scan')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="my">
              <QrCode className="h-4 w-4 mr-2" /> My Code
            </TabsTrigger>
            <TabsTrigger value="scan">
              <ScanLine className="h-4 w-4 mr-2" /> Scan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my" className="mt-4">
            <Card className="p-6 flex flex-col items-center gap-4">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="My contact QR code"
                  className="w-64 h-64 rounded-lg bg-white p-2"
                />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              <div className="text-center">
                <p className="font-semibold">{profile?.name}</p>
                <p className="text-sm text-muted-foreground">{profile?.phone}</p>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Have someone scan this code to add you instantly.
              </p>
              <div className="flex gap-2 w-full">
                <Button variant="outline" className="flex-1" onClick={handleCopy}>
                  <Copy className="h-4 w-4 mr-2" /> Copy
                </Button>
                <Button className="flex-1" onClick={handleShare}>
                  <Share2 className="h-4 w-4 mr-2" /> Share
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="scan" className="mt-4">
            <Card className="p-4 space-y-4">
              <div
                id={scannerElementId}
                className="w-full aspect-square bg-muted rounded-lg overflow-hidden"
              />
              {processing && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Opening chat…
                </div>
              )}
              {!scanning && !processing ? (
                <Button className="w-full" onClick={startScanner}>
                  <ScanLine className="h-4 w-4 mr-2" /> Start Scanning
                </Button>
              ) : !processing ? (
                <Button variant="outline" className="w-full" onClick={stopScanner}>
                  Stop
                </Button>
              ) : null}
              <p className="text-xs text-muted-foreground text-center">
                Point your camera at a Global Chat Connect QR code.
              </p>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
